import { useEffect, useRef, useState } from 'react';
import {
  EBAY_PUBLISH_CARD_LIMIT,
  type EbayPublishCardRequest,
  type EbayPublishPreviewCard,
  type EbayPublishPreviewResponse,
  type EbayPublishResponse,
  type EbayPublishResult,
} from '@tcvs/shared';
import type { ApiResult } from '../auth/api-client';

/**
 * RFC-0005 Phase 2 Publish from Ready to List (owner, 2026-10-30): preview the selected cards, then publish them to
 * eBay through the Listing API. The Publish click is the Approve for eBay step. Cards go a few per request; keep this
 * page open until it finishes.
 */
export interface EbayPublishApi {
  previewEbayPublish: (
    cards: EbayPublishCardRequest[],
  ) => Promise<ApiResult<EbayPublishPreviewResponse>>;
  publishToEbay: (cards: EbayPublishCardRequest[]) => Promise<ApiResult<EbayPublishResponse>>;
}

/** Retries per card when a request runs out of time before the card starts. */
const MAX_NOT_STARTED_RETRIES = 3;
/** Rounds that wait for eBay to report an ended CardDealerPro listing, then list the card. */
const END_PENDING_ROUNDS = 4;
const END_PENDING_WAIT_MS = 15_000;
/** Requests in flight at once; eBay's waits for ended listings overlap instead of adding up. */
export const EBAY_PUBLISH_LANES = 3;

/**
 * Splits work into lanes run side by side. Copies of the same card share a lane, so they are approved one after the
 * other and each gets its own title code (AA, BB, CC).
 */
export function ebayPublishLanes<T>(items: readonly T[], key: (item: T) => string): T[][] {
  const lanes: T[][] = Array.from({ length: EBAY_PUBLISH_LANES }, () => []);
  const laneOf = new Map<string, number>();
  for (const item of items) {
    const group = key(item);
    let lane = laneOf.get(group);
    if (lane === undefined) {
      // The shortest lane takes a new card, so the lanes stay even.
      lane = lanes.reduce(
        (best, current, index) => (current.length < lanes[best]!.length ? index : best),
        0,
      );
      laneOf.set(group, lane);
    }
    lanes[lane]!.push(item);
  }
  return lanes.filter((lane) => lane.length > 0);
}

/** The old listing was ended but eBay had not reported it yet; publishing again lists the card. */
const END_PENDING_TEXT = 'eBay still reports the listing as not ended';

export function ebayEndPending(result: EbayPublishResult | undefined): boolean {
  return (
    (result?.outcome === 'END_UNCONFIRMED' && (result.detail ?? '').startsWith(END_PENDING_TEXT)) ||
    (result?.outcome === 'SKIPPED' &&
      (result.reasons ?? []).some((reason) => reason.startsWith(END_PENDING_TEXT)))
  );
}

/** The old listing was ended and TCVS is waiting for eBay to report it. */
const previewEndPending = (card: EbayPublishPreviewCard) =>
  card.reasons.some((reason) => reason.startsWith(END_PENDING_TEXT));

/**
 * A preview that changed only in its title code: a second copy of the same card is given the next code ("BB") once
 * the first is approved. Publish takes it without asking again.
 */
export function onlyTitleCodeChanged(
  before: EbayPublishPreviewCard | undefined,
  after: EbayPublishPreviewCard,
): boolean {
  if (before === undefined || before.listing === undefined || after.listing === undefined)
    return false;
  const stem = (title: string) => title.replace(/ [A-Z]{2}$/, '');
  const { title: beforeTitle, ...beforeRest } = before.listing;
  const { title: afterTitle, ...afterRest } = after.listing;
  return (
    before.action === after.action &&
    before.takeover?.ebayItemId === after.takeover?.ebayItemId &&
    JSON.stringify(beforeRest) === JSON.stringify(afterRest) &&
    stem(beforeTitle) === stem(afterTitle) &&
    publishableEbayCards([after]).length === 1
  );
}

export function chunkEbayPublish<T>(items: readonly T[]): T[][] {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += EBAY_PUBLISH_CARD_LIMIT)
    chunks.push(items.slice(index, index + EBAY_PUBLISH_CARD_LIMIT));
  return chunks;
}

/** Cards the operator can publish: not held, not done, and not refused by eBay's check. */
export function publishableEbayCards(cards: readonly EbayPublishPreviewCard[]) {
  return cards.filter(
    (card) =>
      (card.action === 'PUBLISH' || card.action === 'TAKEOVER') && card.validation?.ok !== false,
  );
}

const ACTION_LABELS: Readonly<Record<EbayPublishPreviewCard['action'], string>> = {
  PUBLISH: 'New listing',
  TAKEOVER: 'Take over CardDealerPro listing',
  HELD: 'Held',
  DONE: 'Already published',
};

const OUTCOME_LABELS: Readonly<Record<string, string>> = {
  LISTED: 'Listed',
  ALREADY_DONE: 'Already published',
  SKIPPED: 'Held, not published',
  STALE: 'Changed since preview: preview again',
  NOT_STARTED: 'Not started (time limit)',
  REJECTED: 'Rejected by eBay',
  FAILED: 'Failed',
  CREATE_FAILED: 'Old listing ended, new listing failed',
  END_UNCONFIRMED: 'Old listing not confirmed ended; nothing listed',
  SOLD_DURING_TAKEOVER: 'Old listing sold; nothing listed',
};

const BASIS_LABELS = {
  LINKED: 'linked to this card',
  ONE_TO_ONE: 'the only live listing of this card; no other copy is unsold and unlisted',
  OWNER_PAIR: 'paired by you',
  IDENTICAL_COPIES: 'one of several identical listings, each paired with one identical copy',
} as const;

const money = (minor: number | undefined) =>
  minor === undefined ? '-' : `$${(minor / 100).toFixed(2)}`;

function resultDetail(result: EbayPublishResult): string {
  if (result.outcome === 'LISTED') return `eBay item ${result.listingId ?? result.newEbayItemId}`;
  if (result.violations?.length)
    return result.violations.map((violation) => violation.description || violation.code).join('; ');
  return result.detail ?? result.reasons?.join(' ') ?? '';
}

/** eBay serves its photos in sizes ("s-l500"); the enlarged view asks for the largest. */
export const largeEbayImage = (url: string) => url.replace(/\/s-l\d+(\.\w+)$/, '/s-l1600$1');

/** A small photo that shows large on hover, or on tap and keyboard focus. */
function ZoomImage({ src, large, label }: { src: string; large?: string; label: string }) {
  return (
    <span className="tcvs-zoom" tabIndex={0} aria-label={`${label}: show larger`}>
      <img className="tcvs-zoom-thumb" src={src} alt={label} loading="lazy" />
      <img className="tcvs-zoom-large" src={large ?? src} alt="" loading="lazy" />
    </span>
  );
}

export function EbayPublishPanel({
  api,
  cardIds,
  titles,
  images = {},
  onClose,
  onPublished,
  wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
}: {
  api: EbayPublishApi;
  cardIds: readonly string[];
  titles: Readonly<Record<string, string>>;
  /** Each card's front photo, to compare with the CardDealerPro listing. */
  images?: Readonly<Record<string, string>>;
  onClose: () => void;
  onPublished: () => void;
  wait?: (ms: number) => Promise<unknown>;
}): JSX.Element {
  const [previews, setPreviews] = useState<Record<string, EbayPublishPreviewCard>>({});
  const [pairs, setPairs] = useState<Record<string, string>>({});
  /** Cards the operator confirmed are not on CardDealerPro, so they list new. */
  const [listNew, setListNew] = useState<Record<string, boolean>>({});
  const [results, setResults] = useState<Record<string, EbayPublishResult>>({});
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState<'preview' | 'publish' | 'waiting' | null>(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const request = (cardId: string): EbayPublishCardRequest =>
    pairs[cardId]
      ? { cardId, ebayItemId: pairs[cardId] }
      : listNew[cardId]
        ? { cardId, listNew: true }
        : { cardId };

  /** Cards held for the operator: shown alone so they can be settled in one place. */
  const [heldOnly, setHeldOnly] = useState(false);

  const runPreview = async (
    ids: readonly string[],
    override: Readonly<Record<string, EbayPublishCardRequest>> = {},
  ) => {
    setBusy('preview');
    setProgress({ done: 0, total: ids.length });
    const failures: string[] = [];
    let done = 0;
    await Promise.all(
      ebayPublishLanes(chunkEbayPublish(ids), (chunk) => chunk.join()).map(async (lane) => {
        for (const chunk of lane) {
          const result = await api.previewEbayPublish(
            chunk.map((cardId) => override[cardId] ?? request(cardId)),
          );
          if (result.ok) {
            const cards = result.data.cards;
            setPreviews((current) => ({
              ...current,
              ...Object.fromEntries(cards.map((card) => [card.cardId, card])),
            }));
          } else {
            failures.push(`${chunk.join(', ')}: ${result.message ?? result.error}`);
          }
          done += chunk.length;
          setProgress({ done, total: ids.length });
        }
      }),
    );
    setErrors(failures);
    setBusy(null);
  };

  const panel = useRef<HTMLElement>(null);
  useEffect(() => {
    // The panel opens above the queue, so bring it into view from the button that opened it.
    panel.current?.scrollIntoView?.({ block: 'start' });
    void runPreview(cardIds);
    // The panel previews its selection once when it opens.
  }, []);

  const ordered = cardIds.flatMap((cardId) => (previews[cardId] ? [previews[cardId]!] : []));
  /** The card already using an eBay item in this selection, if not this card. */
  const pairedElsewhere = (cardId: string, itemId: string | undefined) =>
    itemId === undefined || itemId === ''
      ? undefined
      : cardIds.find(
          (other) =>
            other !== cardId &&
            (pairs[other] === itemId || previews[other]?.takeover?.ebayItemId === itemId),
        );
  // A card that changed since its preview (a second copy given the next title code) is offered again.
  const publishable = publishableEbayCards(ordered).filter(
    (card) =>
      (!results[card.cardId] || results[card.cardId]!.outcome === 'STALE') &&
      pairedElsewhere(card.cardId, card.takeover?.ebayItemId) === undefined,
  );
  const takeovers = publishable.filter((card) => card.action === 'TAKEOVER').length;

  const publishCards = async (
    queue: EbayPublishCardRequest[],
    failures: string[],
  ): Promise<Record<string, EbayPublishResult>> => {
    const finished: Record<string, EbayPublishResult> = {};
    const attempts: Record<string, number> = {};
    /** The preview each card was published from, to tell a title-code change from any other. */
    const seen: Record<string, EbayPublishPreviewCard | undefined> = { ...previews };
    const total = queue.length;
    setProgress({ done: 0, total });
    let done = 0;
    const stem = (cardId: string) =>
      (previews[cardId]?.listing?.title ?? cardId).replace(/ [A-Z]{2}$/, '');
    const runLane = async (lane: EbayPublishCardRequest[]) => {
      while (lane.length > 0) {
        const chunk = lane.splice(0, EBAY_PUBLISH_CARD_LIMIT);
        const result = await api.publishToEbay(chunk);
        if (!result.ok) {
          failures.push(
            `${chunk.map((card) => card.cardId).join(', ')}: ${result.message ?? result.error}`,
          );
          done += chunk.length;
          setProgress({ done, total });
          continue;
        }
        for (const outcome of result.data.results) {
          const card = chunk.find((entry) => entry.cardId === outcome.cardId);
          attempts[outcome.cardId] = (attempts[outcome.cardId] ?? 0) + 1;
          if (
            outcome.outcome === 'NOT_STARTED' &&
            card !== undefined &&
            (attempts[outcome.cardId] ?? 0) <= MAX_NOT_STARTED_RETRIES
          ) {
            lane.push(card);
            continue;
          }
          if (
            outcome.outcome === 'STALE' &&
            outcome.preview !== undefined &&
            card !== undefined &&
            (attempts[outcome.cardId] ?? 0) <= MAX_NOT_STARTED_RETRIES &&
            onlyTitleCodeChanged(seen[outcome.cardId], outcome.preview)
          ) {
            seen[outcome.cardId] = outcome.preview;
            setPreviews((current) => ({ ...current, [outcome.cardId]: outcome.preview! }));
            lane.push({ ...card, digest: outcome.preview.digest });
            continue;
          }
          done += 1;
          finished[outcome.cardId] = outcome;
          setResults((current) => ({ ...current, [outcome.cardId]: outcome }));
          if (outcome.outcome === 'STALE' && outcome.preview)
            setPreviews((current) => ({ ...current, [outcome.cardId]: outcome.preview! }));
        }
        setProgress({ done, total });
      }
    };
    await Promise.all(
      ebayPublishLanes(queue, (card) => stem(card.cardId)).map((lane) => runLane(lane)),
    );
    return finished;
  };

  const publish = async () => {
    setBusy('publish');
    const failures: string[] = [];
    let finished = await publishCards(
      publishable.map((card) => ({ ...request(card.cardId), digest: card.digest })),
      failures,
    );
    // Cards whose old listing ended but eBay had not reported it yet: wait, check again, then list them.
    let pending = Object.values(finished)
      .filter(ebayEndPending)
      .map((result) => result.cardId);
    for (let round = 0; round < END_PENDING_ROUNDS && pending.length > 0; round += 1) {
      setBusy('waiting');
      await wait(END_PENDING_WAIT_MS);
      setBusy('publish');
      const retry: EbayPublishCardRequest[] = [];
      const stillEnding: string[] = [];
      for (const chunk of chunkEbayPublish(pending)) {
        const result = await api.previewEbayPublish(chunk.map(request));
        if (!result.ok) continue;
        for (const card of result.data.cards) {
          setPreviews((current) => ({ ...current, [card.cardId]: card }));
          if (publishableEbayCards([card]).length === 1)
            retry.push({ ...request(card.cardId), digest: card.digest });
          else if (previewEndPending(card)) stillEnding.push(card.cardId);
        }
      }
      finished = await publishCards(retry, failures);
      pending = [
        ...stillEnding,
        ...Object.values(finished)
          .filter(ebayEndPending)
          .map((result) => result.cardId),
      ];
    }
    setErrors(failures);
    setBusy(null);
    onPublished();
  };

  const listed = Object.values(results).filter((result) => result.outcome === 'LISTED').length;

  return (
    <section ref={panel} className="tcvs-panel" aria-label="Publish to eBay">
      <div className="tcvs-toolbar">
        <h3>Publish to eBay</h3>
        <button type="button" className="tcvs-linklike" onClick={onClose} disabled={busy !== null}>
          Close
        </button>
      </div>
      <p className="tcvs-muted">
        Publishing approves each card for eBay and lists it through the eBay API. A card already
        live through CardDealerPro is taken over: its old listing is ended first, then TCVS lists
        it. Keep this page open until it finishes.
      </p>
      {busy !== null ? (
        <p role="status">
          {busy === 'waiting'
            ? 'Waiting for eBay to confirm the old listings ended…'
            : `${busy === 'preview' ? 'Checking' : 'Publishing'} ${progress.done} of ${progress.total}…`}
        </p>
      ) : null}
      {errors.map((message) => (
        <p key={message} className="tcvs-error">
          {message}
        </p>
      ))}
      <label>
        <input
          type="checkbox"
          checked={heldOnly}
          onChange={(event) => setHeldOnly(event.target.checked)}
        />{' '}
        {`Show held cards only (${ordered.filter((card) => card.action === 'HELD').length})`}
      </label>
      <div className="tcvs-table-wrap">
        <table className="tcvs-table">
          <thead>
            <tr>
              <th>Card</th>
              <th>Price</th>
              <th>Shipping</th>
              <th>Publish</th>
              <th>eBay check</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {cardIds
              .filter((cardId) => !heldOnly || previews[cardId]?.action === 'HELD')
              .map((cardId) => {
                const card = previews[cardId];
                const result = results[cardId];
                const duplicateHold = card?.reasons.some((reason) =>
                  reason.startsWith('Possible duplicate'),
                );
                const possibleListing = card?.reasons.some((reason) =>
                  reason.startsWith('Possible CardDealerPro listing'),
                );
                return (
                  <tr key={cardId}>
                    <td>
                      {images[cardId] === undefined ? null : (
                        <ZoomImage src={images[cardId]!} label="TCVS photo" />
                      )}
                      {card?.listing?.title ?? titles[cardId] ?? cardId}
                    </td>
                    <td>{money(card?.listing?.priceMinor)}</td>
                    <td>{card?.listing?.shippingPolicy ?? '-'}</td>
                    <td>
                      {card === undefined ? (
                        'Checking…'
                      ) : (
                        <>
                          <strong>{ACTION_LABELS[card.action]}</strong>
                          {card.takeover ? (
                            <div className="tcvs-muted">
                              eBay item {card.takeover.ebayItemId} (
                              {BASIS_LABELS[card.takeover.basis]}); CardDealerPro SKU{' '}
                              {card.takeover.old?.sku ?? '-'} at{' '}
                              {money(card.takeover.old?.priceMinor)}
                              {card.takeover.priceChanged ? ': price differs from TCVS' : ''}
                            </div>
                          ) : null}
                          {card.reasons.map((reason) => (
                            <div key={reason} className="tcvs-muted">
                              {reason}
                            </div>
                          ))}
                          {(card.notes ?? []).map((note) => (
                            <div key={note} className="tcvs-muted">
                              {note}
                            </div>
                          ))}
                          {pairedElsewhere(cardId, pairs[cardId] || card.takeover?.ebayItemId) !==
                          undefined ? (
                            <div className="tcvs-error">
                              eBay item {pairs[cardId] || card.takeover?.ebayItemId} is already
                              paired with another card in this selection.
                            </div>
                          ) : null}
                          {card.action === 'HELD' && (card.candidates ?? []).length > 0 ? (
                            <ul className="tcvs-plain-list">
                              {(card.candidates ?? []).map((candidate) => (
                                <li key={candidate.ebayItemId}>
                                  <button
                                    type="button"
                                    className="tcvs-btn-compact"
                                    disabled={
                                      busy !== null ||
                                      pairedElsewhere(cardId, candidate.ebayItemId) !== undefined
                                    }
                                    onClick={() => {
                                      setListNew((current) => ({ ...current, [cardId]: false }));
                                      setPairs((current) => ({
                                        ...current,
                                        [cardId]: candidate.ebayItemId,
                                      }));
                                      void runPreview([cardId], {
                                        [cardId]: { cardId, ebayItemId: candidate.ebayItemId },
                                      });
                                    }}
                                  >
                                    Pair
                                  </button>{' '}
                                  {candidate.imageUrl === undefined ? null : (
                                    <ZoomImage
                                      src={candidate.imageUrl}
                                      large={largeEbayImage(candidate.imageUrl)}
                                      label="CardDealerPro photo"
                                    />
                                  )}{' '}
                                  <a
                                    href={`https://www.ebay.com/itm/${candidate.ebayItemId}`}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {candidate.title ?? `eBay item ${candidate.ebayItemId}`}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          ) : null}
                          {possibleListing || listNew[cardId] ? (
                            <label>
                              <input
                                type="checkbox"
                                checked={listNew[cardId] === true}
                                disabled={busy !== null || Boolean(pairs[cardId])}
                                onChange={(event) =>
                                  setListNew((current) => ({
                                    ...current,
                                    [cardId]: event.target.checked,
                                  }))
                                }
                              />{' '}
                              Not on CardDealerPro: list it new{' '}
                              <button
                                type="button"
                                className="tcvs-btn-compact"
                                disabled={busy !== null || Boolean(pairs[cardId])}
                                onClick={() => void runPreview([cardId])}
                              >
                                Check
                              </button>
                            </label>
                          ) : null}
                          {duplicateHold || possibleListing || pairs[cardId] !== undefined ? (
                            <label>
                              Pair with eBay item{' '}
                              <input
                                value={pairs[cardId] ?? ''}
                                inputMode="numeric"
                                onChange={(event) =>
                                  setPairs((current) => ({
                                    ...current,
                                    [cardId]: event.target.value.trim(),
                                  }))
                                }
                              />
                              <button
                                type="button"
                                className="tcvs-btn-compact"
                                disabled={
                                  busy !== null ||
                                  !/^\d{9,15}$/.test(pairs[cardId] ?? '') ||
                                  pairedElsewhere(cardId, pairs[cardId]) !== undefined
                                }
                                onClick={() => void runPreview([cardId])}
                              >
                                Check
                              </button>
                            </label>
                          ) : null}
                        </>
                      )}
                    </td>
                    <td>
                      {card?.validation === undefined
                        ? '-'
                        : card.validation.ok
                          ? 'OK'
                          : card.validation.errors.join('; ')}
                    </td>
                    <td>
                      {result ? (
                        <>
                          {OUTCOME_LABELS[result.outcome] ?? result.outcome}
                          <div className="tcvs-muted">{resultDetail(result)}</div>
                        </>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      <div className="tcvs-toolbar">
        <button
          type="button"
          disabled={busy !== null || publishable.length === 0}
          onClick={() => void publish()}
        >
          {`Publish ${publishable.length} ${publishable.length === 1 ? 'card' : 'cards'} to eBay`}
          {takeovers > 0 ? ` (${takeovers} ending a CardDealerPro listing first)` : ''}
        </button>
        {Object.keys(results).length > 0 ? (
          <span className="tcvs-muted">{listed} listed on eBay.</span>
        ) : null}
      </div>
    </section>
  );
}
